import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"

import {
  ADMIN_QUERY_KEYS,
  type AdminAccountsResponse,
  type TikTokAccountsListResponse,
} from "./adminTypes"

// ---------------------------------------------------------------------------
// Admin accounts list
// ---------------------------------------------------------------------------

export function useAdminAccounts() {
  return useQuery<AdminAccountsResponse>({
    queryKey: ADMIN_QUERY_KEYS.accounts,
    queryFn: () => apiFetch<AdminAccountsResponse>("/api/admin/accounts"),
    retry: false,
  })
}

export function useTikTokAccountsList() {
  return useQuery<TikTokAccountsListResponse>({
    queryKey: [...ADMIN_QUERY_KEYS.tiktokAccounts, "operational"] as const,
    queryFn: () =>
      apiFetch<TikTokAccountsListResponse>(
        "/auth/tiktok/accounts?include_deleted=false",
      ),
    retry: false,
  })
}

export function useAssignAccountToUserQuick() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { accountId: number; userId: number }) => {
      const fd = new FormData()
      fd.append("user_id", String(data.userId))
      return apiFetch<{ success: boolean; message: string }>(
        `/api/admin/accounts/${data.accountId}/assign-to-user`,
        { method: "POST", body: fd },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.all })
    },
  })
}

export function useUpdateAccountNetwork() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: { accountId: number; network: string }) => {
      const fd = new FormData()
      fd.append("network", data.network)
      return apiFetch<{ success: boolean; message: string }>(
        `/api/admin/accounts/${data.accountId}/network`,
        { method: "PUT", body: fd },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.all })
    },
  })
}

export function useDeleteTikTokAccount() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (accountId: number) =>
      apiFetch<{ success: boolean; message: string }>(
        `/api/admin/accounts/${accountId}`,
        { method: "DELETE" },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ADMIN_QUERY_KEYS.all })
    },
  })
}

// ---------------------------------------------------------------------------
// TikTok connect / refresh
// ---------------------------------------------------------------------------

export function useConnectTikTok() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ auth_url: string }>("/auth/tiktok/connect"),
  })
}

export function useRefreshTikTokAccountsList() {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ success: boolean; redirect_url: string }>(
        "/api/admin/tiktok/refresh-select-accounts",
        { method: "POST" },
      ),
  })
}
